import React, { useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'
import 'react-dates/initialize'
import { DayPickerSingleDateController } from 'react-dates'
import { Button } from '@/components/atoms/button'
import { Select, Option } from '@/components/atoms/select'
import { fetchBaseRates, fetchRoomRateDisplay } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { calendarNavNext, calendarNavPrev, renderCalendarDay } from '@/components/pages/room-manager/_room-manager/date-picker'
import { GUEST_COUNT_OPTIONS, resolveAdoptedPriceType } from '@/models/room-rate/base-rate'
import type { BaseRateGuestCount, BaseRatePriceType } from '@/models/room-rate/base-rate'
import {
  loadRoomRateDisplayPrefs,
  saveRoomRateDisplayPrefs,
  toApiRoomRateDisplayParams,
  toRoomRateDisplayTable,
} from '@/models/room-rate/room-rate-display'
import type { RoomRateDisplayDayColumn, RoomRateDisplayRow } from '@/models/room-rate/room-rate-display'
import { formatRoomRateAmount } from '@/models/room-rate/format'
import { getRateCalendarWeekdayTextColor } from '@/models/room-rate/rate-calendar'
import { roomRateModalOverlayStyle, roomRatePillSelectStyle } from '@/components/pages/setting/room-rate/shared-styles'

type Props = {
  initialDate?: string | Dayjs | Date
  onClose: () => void
}

export const RoomRateDisplayModal: React.FC<Props> = ({ initialDate, onClose }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const storedPrefs = useMemo(() => loadRoomRateDisplayPrefs(), [])
  const [startDate, setStartDate] = useState<Dayjs>(() => dayjs(initialDate || undefined).startOf('day'))
  const [priceType, setPriceType] = useState<BaseRatePriceType>(storedPrefs?.priceType ?? 'rc')
  const [guestCount, setGuestCount] = useState<BaseRateGuestCount>(storedPrefs?.guestCount ?? 1)
  const [prefsResolved, setPrefsResolved] = useState(storedPrefs !== null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [columns, setColumns] = useState<RoomRateDisplayDayColumn[]>([])
  const [rows, setRows] = useState<RoomRateDisplayRow[]>([])
  const requestId = useRef(0)

  const persistPrefs = (nextType: BaseRatePriceType, nextGuest: BaseRateGuestCount) => {
    saveRoomRateDisplayPrefs({ priceType: nextType, guestCount: nextGuest })
  }

  const onChangePriceType = (next: BaseRatePriceType) => {
    setPriceType(next)
    persistPrefs(next, guestCount)
  }

  const onChangeGuestCount = (next: BaseRateGuestCount) => {
    setGuestCount(next)
    persistPrefs(priceType, next)
  }

  // 保存が無いときだけ、マウント時に採用区分を取る
  useEffect(() => {
    if (storedPrefs) return
    let cancelled = false
    const loadAdopted = async () => {
      try {
        const rows = (await fetchBaseRates({ sheetChargeType: 'rc' })) || []
        if (cancelled) return
        setPriceType(resolveAdoptedPriceType(rows))
      } catch {
        // ベースレート GET は設定画面の権限が要る。客室管理だけの権限でも開けるよう、alert せず室単価（初期値）のまま表示する
      } finally {
        if (!cancelled) setPrefsResolved(true)
      }
    }
    loadAdopted()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!prefsResolved) return
    const currentRequestId = ++requestId.current
    const load = async () => {
      setIsLoading(true)
      try {
        const from = startDate.format('YYYY-MM-DD')
        const res = await fetchRoomRateDisplay(toApiRoomRateDisplayParams(from, priceType, guestCount))
        if (currentRequestId !== requestId.current) return
        const table = toRoomRateDisplayTable(res || { roomTypes: [] })
        setColumns(table.columns)
        setRows(table.rows)
      } catch (e) {
        if (currentRequestId !== requestId.current) return
        setColumns([])
        setRows([])
        errorHandler(e)
      } finally {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
      }
    }
    load()
  }, [startDate, priceType, guestCount, prefsResolved])

  const weekdayLabel = (weekday: number) => t(`rateCalendar.weekday.${weekday}`)

  return (
    <div css={[roomRateModalOverlayStyle, displayOverlayStyle]}>
      <div css={modalStyle}>
        <div css={headerStyle}>
          <div css={titleStyle}>{t('Room rate')}</div>
        </div>

        <div css={bodyStyle}>
          <div css={weekNavStyle}>
            <button type="button" css={weekButtonStyle} onClick={() => setStartDate(d => d.subtract(7, 'day'))}>
              {t('Prev week')}
            </button>
            <button type="button" css={weekButtonStyle} onClick={() => setStartDate(dayjs().startOf('day'))}>
              {t('Today')}
            </button>
            <button type="button" css={weekButtonStyle} onClick={() => setStartDate(d => d.add(7, 'day'))}>
              {t('Next week')}
            </button>
          </div>
          <div css={controlsRowStyle}>
            <div css={dateNavStyle}>
              <button type="button" css={chevronButtonStyle} onClick={() => setStartDate(d => d.subtract(1, 'day'))}>
                <img src={require('@/static/images/arrow_right_gray_nospace.svg')} alt="" css={chevronLeftImgStyle} />
              </button>
              <div css={dateLabelWrapStyle}>
                {/* 開いている間の再クリックは onOutsideClick で閉じた直後に開き直るので、トグルにせず常に開く */}
                <span css={dateLabelStyle} onClick={() => setIsCalendarOpen(true)}>
                  {startDate.format(t('YYYY-MM-DD'))}
                </span>
                {isCalendarOpen && (
                  <div css={calendarPopoverStyle}>
                    <DayPickerSingleDateController
                      date={moment(startDate.format('YYYY-MM-DD'))}
                      onDateChange={date => {
                        if (date) setStartDate(dayjs(date.format('YYYY-MM-DD')))
                        setIsCalendarOpen(false)
                      }}
                      focused
                      onFocusChange={() => undefined}
                      onOutsideClick={() => setIsCalendarOpen(false)}
                      numberOfMonths={1}
                      initialVisibleMonth={null}
                      isOutsideRange={() => false}
                      monthFormat={t('YYYY-MM')}
                      hideKeyboardShortcutsPanel
                      navPrev={calendarNavPrev}
                      navNext={calendarNavNext}
                      renderCalendarDay={renderCalendarDay}
                    />
                  </div>
                )}
              </div>
              <button type="button" css={chevronButtonStyle} onClick={() => setStartDate(d => d.add(1, 'day'))}>
                <img src={require('@/static/images/arrow_right_gray_nospace.svg')} alt="" css={chevronRightImgStyle} />
              </button>
            </div>
            <div css={priceTypeWrapStyle}>
              <span css={priceTypeLabelStyle}>{t('baseRate.priceTypeLabel')}</span>
              <Select value={priceType} onChange={v => onChangePriceType(v as BaseRatePriceType)} customStyle={displayPriceTypeSelectStyle}>
                <Option value="rc">{t('baseRate.priceType.rc')}</Option>
                <Option value="perPerson">{t('baseRate.priceType.perPerson')}</Option>
              </Select>
              {priceType === 'perPerson' && (
                <Select
                  value={String(guestCount)}
                  onChange={v => onChangeGuestCount(Number(v) as BaseRateGuestCount)}
                  customStyle={displayGuestCountSelectStyle}
                >
                  {GUEST_COUNT_OPTIONS.map(count => (
                    <Option key={count} value={String(count)}>
                      {count === 10 ? t('baseRate.guestCount.10plus') : t('baseRate.guestCount.n', { count })}
                    </Option>
                  ))}
                </Select>
              )}
            </div>
          </div>
          <div css={tableCardStyle}>
            {isLoading ? (
              <div css={loadingStyle}>{t('Loading')}</div>
            ) : (
              <table css={tableStyle}>
                <thead>
                  <tr>
                    <th css={dateCornerStyle} />
                    {columns.map(col => (
                      <th key={col.dateKey} css={dateHeaderStyle}>
                        <div css={weekdayTextStyle}>{weekdayLabel(col.weekday)}</div>
                        <div css={dayNumberStyle} style={{ color: getRateCalendarWeekdayTextColor(col.weekday) }}>
                          {col.day}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th css={cornerHeaderStyle}>{t('Room type')}</th>
                    {columns.map(col => (
                      <th key={`${col.dateKey}-rank`} css={rankHeaderStyle} style={{ background: col.headerColor }}>
                        {col.rankShortName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.roomTypeId}>
                      <td css={roomTypeCellStyle}>{row.roomTypeName}</td>
                      {row.amounts.map((amount, i) => (
                        <td key={`${row.roomTypeId}-${columns[i]?.dateKey}`} css={amountCellStyle} style={{ background: columns[i]?.tint }}>
                          <span css={amountTextStyle}>{formatRoomRateAmount(amount)}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div css={footerStyle}>
          <Button buttonType={3} width={110} height={38} onClick={onClose}>
            {t('Back')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// アサイン室料金は設定モーダルと違い、画面縦中央＋やや高いパネルにする
const displayOverlayStyle = css({
  zIndex: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 0',
  boxSizing: 'border-box',
})

const modalStyle = css({
  width: 'calc(100% - 80px)',
  maxWidth: 1200,
  height: 'calc(100vh - 120px)',
  maxHeight: 720,
  background: '#fff',
  borderRadius: 5,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: '0px 0px 6px rgba(0,0,0,0.1)',
  flexShrink: 0,
})

const headerStyle = css({
  height: 60,
  padding: '17px 32px 0',
  flexShrink: 0,
  boxSizing: 'border-box',
})

const titleStyle = css({
  fontSize: 18,
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: '27px',
})

const weekNavStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 8,
})

const weekButtonStyle = css({
  width: 48,
  height: 24,
  border: '1px solid #ccc',
  borderRadius: 20,
  background: '#fff',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
  cursor: 'pointer',
  padding: 0,
  lineHeight: '24px',
  textAlign: 'center',
  '&:hover': {
    opacity: 0.85,
  },
})

const controlsRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 32,
  marginBottom: 28,
  flexWrap: 'wrap',
})

const dateNavStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const chevronButtonStyle = css({
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  lineHeight: 0,
  display: 'flex',
  alignItems: 'center',
  width: 7,
})

const chevronLeftImgStyle = css({
  height: 14,
  width: 7,
  transform: 'rotate(180deg)',
})

const chevronRightImgStyle = css({
  height: 14,
  width: 7,
})

const dateLabelWrapStyle = css({
  position: 'relative',
})

const dateLabelStyle = css({
  fontSize: 16,
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: '24px',
  cursor: 'pointer',
})

const calendarPopoverStyle = css({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  // 日付はモーダル左端に近く、中央寄せだと body（overflow: auto）の左で切れるため左端揃えにする
  left: 0,
  // 下の料金表（tableCard）より前面に出す
  zIndex: 10,
})

const priceTypeWrapStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const priceTypeLabelStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  color: '#676767',
  whiteSpace: 'nowrap',
  lineHeight: '21px',
})

const displaySelectStyle = css(roomRatePillSelectStyle, {
  '.ant-select-selection__rendered': {
    paddingLeft: '0 !important',
  },
  '.ant-select-arrow': {
    right: '16px !important',
  },
})

const displayPriceTypeSelectStyle = css(displaySelectStyle, {
  width: 111,
})

const displayGuestCountSelectStyle = css(displaySelectStyle, {
  width: 81,
})

const bodyStyle = css({
  flex: 1,
  background: '#F2F2F2',
  padding: '24px 32px',
  overflow: 'auto',
})

const tableCardStyle = css({
  background: '#fff',
  // カレンダー表の四隅
  borderRadius: 5,
  // auto だと角丸クリップが効きにくいため hidden（縦スクロールは body 側）
  overflow: 'hidden',
  minHeight: 200,
})

const loadingStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
  fontSize: 14,
  color: '#676767',
})

const tableStyle = css({
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  // 固定1136pxだと狭い画面で横スクロールになるため親幅に追従
  width: '100%',
})

const dateCornerStyle = css({
  // 日付列を優先し、部屋タイプ列を抑えて横はみ出しを防ぐ
  width: '16%',
  minWidth: 120,
  maxWidth: 180,
  height: 53,
  padding: 0,
  background: '#fff',
  border: '1px solid #F2F2F2',
})

const cornerHeaderStyle = css({
  width: '16%',
  minWidth: 120,
  maxWidth: 180,
  height: 27,
  background: '#F2F2F2',
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'left',
  padding: '6px 15px 6px 14px',
  border: '1px solid #F2F2F2',
  verticalAlign: 'middle',
  lineHeight: '15px',
})

const dateHeaderStyle = css({
  height: 53,
  padding: 0,
  border: '1px solid #F2F2F2',
  background: '#fff',
  verticalAlign: 'middle',
  textAlign: 'center',
})

const weekdayTextStyle = css({
  fontSize: 10,
  fontWeight: 'bold',
  color: '#A3A3A3',
  textAlign: 'center',
  lineHeight: '14px',
})

const dayNumberStyle = css({
  fontSize: 16,
  fontWeight: 'bold',
  textAlign: 'center',
  lineHeight: '21px',
  marginTop: 2,
})

const rankHeaderStyle = css({
  height: 27,
  padding: '6px 15px 6px 14px',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#fff',
  textAlign: 'left',
  border: '1px solid #F2F2F2',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: '15px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const roomTypeCellStyle = css({
  width: '16%',
  minWidth: 120,
  maxWidth: 180,
  height: 37,
  padding: '7px 16px 6px 14px',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
  textAlign: 'left',
  border: '1px solid #F2F2F2',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const amountCellStyle = css({
  height: 37,
  // ランク見出しと同じ左余白にして、列内の左揃えを揃える
  padding: '8px 15px 8px 14px',
  border: '1px solid #F2F2F2',
  verticalAlign: 'middle',
  textAlign: 'left',
})

const amountTextStyle = css({
  display: 'block',
  width: '100%',
  fontSize: 14,
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: '21px',
  textAlign: 'left',
})

const footerStyle = css({
  height: 75,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 32px',
  background: '#fff',
  boxShadow: '0px 0px 6px rgba(0,0,0,0.1)',
})
